import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    RefreshCw,
    Inbox,
    Loader2,
    Paperclip,
    AlertTriangle,
    PenSquare,
    CornerUpLeft,
    X,
    Settings,
    User as UserIcon,
} from "lucide-react";
import api, { formatApiError } from "./api";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        if (sameDay)
            return d.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
            });
        return d.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function senderName(msg) {
    return msg.from_name || (msg.from || "").split("@")[0] || "(unknown)";
}

export default function PortalInbox() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [needsSetup, setNeedsSetup] = useState(false);
    const [active, setActive] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/portal/inbox?limit=30");
            setMessages(data.messages || []);
            setNeedsSetup(false);
        } catch (e) {
            const msg = formatApiError(e?.response?.data?.detail) || e.message;
            if (e?.response?.status === 400 && /Mailbox not configured/i.test(msg)) {
                setNeedsSetup(true);
            } else {
                setErr(msg);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (needsSetup) {
        return (
            <div
                className="pr-glass-strong rounded-3xl p-10 text-center"
                data-testid="inbox-needs-setup"
            >
                <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-500/10 border border-blue-400/30 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-blue-300" />
                </div>
                <h2 className="mt-5 font-outfit text-2xl font-bold text-white">
                    Connect your mailbox
                </h2>
                <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
                    Add your IMAP &amp; SMTP credentials to start reading and
                    sending mail from inside the portal. They're encrypted at
                    rest with AES.
                </p>
                <Link
                    to="/portal/mailbox-setup"
                    className="btn-primary mt-6"
                    data-testid="inbox-go-setup"
                >
                    Configure mailbox
                </Link>
            </div>
        );
    }

    return (
        <div className="grid lg:grid-cols-[400px_1fr] gap-6" data-testid="inbox-page">
            {/* List */}
            <div className="pr-glass rounded-2xl flex flex-col min-h-[60vh] max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <Inbox className="w-4 h-4 text-blue-300" />
                        <h2 className="font-outfit text-base font-semibold text-white">
                            Inbox
                        </h2>
                        <span className="text-[11px] font-mono-pr text-slate-500">
                            {messages.length} latest
                        </span>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="w-8 h-8 rounded-lg pr-glass flex items-center justify-center text-slate-300 hover:text-white"
                        title="Refresh"
                        data-testid="inbox-refresh"
                    >
                        {loading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="w-3.5 h-3.5" />
                        )}
                    </button>
                </div>

                {err && (
                    <div className="m-4 text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-lg px-3 py-2 flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span data-testid="inbox-error">{err}</span>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto" data-testid="inbox-list">
                    {!err && !loading && messages.length === 0 && (
                        <div className="p-10 text-center text-sm text-slate-500">
                            No messages yet.
                        </div>
                    )}
                    {messages.map((m) => {
                        const selected = active?.uid === m.uid;
                        return (
                            <button
                                key={m.uid}
                                onClick={() => setActive(m)}
                                className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors flex gap-3 ${
                                    selected
                                        ? "bg-blue-500/10"
                                        : "hover:bg-white/[0.03]"
                                }`}
                                data-testid={`inbox-row-${m.uid}`}
                            >
                                <div
                                    className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                        m.seen
                                            ? "bg-slate-600"
                                            : "bg-emerald-400 shadow-[0_0_10px_rgba(34,197,94,0.7)]"
                                    }`}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span
                                            className={`text-sm truncate ${
                                                m.seen
                                                    ? "text-slate-300"
                                                    : "text-white font-semibold"
                                            }`}
                                        >
                                            {senderName(m)}
                                        </span>
                                        <span className="text-[10px] font-mono-pr text-slate-500 flex-shrink-0">
                                            {fmtDate(m.date)}
                                        </span>
                                    </div>
                                    <div className="text-sm text-slate-200 truncate mt-0.5">
                                        {m.subject}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate mt-0.5">
                                        {m.preview}
                                    </div>
                                    {m.has_attachments && (
                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-blue-300 font-mono-pr">
                                            <Paperclip className="w-3 h-3" />
                                            attachment
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Reader / placeholder */}
            <div className="pr-glass-strong rounded-2xl min-h-[60vh] max-h-[80vh] overflow-hidden flex flex-col">
                <AnimatePresence mode="wait">
                    {active ? (
                        <MessageView
                            key={active.uid}
                            initial={active}
                            onClose={() => setActive(null)}
                            onReply={(m) =>
                                navigate("/portal/compose", {
                                    state: { reply: m },
                                })
                            }
                        />
                    ) : (
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex-1 flex items-center justify-center text-center text-slate-500 p-10"
                        >
                            <div>
                                <div className="w-12 h-12 mx-auto rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
                                    <Inbox className="w-5 h-5 text-blue-300" />
                                </div>
                                <p className="mt-4 text-sm">
                                    Select a message to read
                                </p>
                                <Link
                                    to="/portal/compose"
                                    className="btn-secondary mt-5 inline-flex"
                                    data-testid="inbox-compose-shortcut"
                                >
                                    <PenSquare className="w-4 h-4" />
                                    New message
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

function MessageView({ initial, onClose, onReply }) {
    const [msg, setMsg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr("");
            try {
                const { data } = await api.get(
                    `/portal/messages/${encodeURIComponent(initial.uid)}`
                );
                if (!cancelled) setMsg(data);
            } catch (e) {
                if (!cancelled)
                    setErr(formatApiError(e?.response?.data?.detail) || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [initial.uid]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col h-full"
            data-testid="message-view"
        >
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-white/5">
                <div className="min-w-0">
                    <h3 className="font-outfit text-lg font-semibold text-white truncate">
                        {initial.subject}
                    </h3>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                        <UserIcon className="w-3.5 h-3.5 text-blue-300" />
                        <span className="truncate">{initial.from}</span>
                        <span>·</span>
                        <span className="font-mono-pr">{fmtDate(initial.date)}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={() => onReply(msg || initial)}
                        className="btn-primary py-2 px-3 text-xs"
                        data-testid="message-reply"
                    >
                        <CornerUpLeft className="w-3.5 h-3.5" />
                        Reply
                    </button>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg pr-glass flex items-center justify-center text-slate-300 hover:text-white"
                        data-testid="message-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {loading && (
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                )}
                {err && (
                    <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-lg px-3 py-2">
                        {err}
                    </div>
                )}
                {msg && (
                    <>
                        {msg.html ? (
                            <div
                                className="prose prose-invert max-w-none text-sm text-slate-200"
                                style={{ wordBreak: "break-word" }}
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(msg.html),
                                }}
                                data-testid="message-html"
                            />
                        ) : (
                            <pre className="text-sm text-slate-200 whitespace-pre-wrap font-inter leading-relaxed">
                                {msg.text}
                            </pre>
                        )}
                        {msg.attachments?.length > 0 && (
                            <div className="mt-6 pt-4 border-t border-white/5">
                                <p className="font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                                    Attachments
                                </p>
                                <ul className="space-y-1.5 text-sm">
                                    {msg.attachments.map((a, i) => (
                                        <li
                                            key={i}
                                            className="flex items-center gap-2 text-slate-300"
                                        >
                                            <Paperclip className="w-3.5 h-3.5 text-blue-300" />
                                            {a.name}
                                            <span className="text-[10px] text-slate-500 font-mono-pr">
                                                {Math.round(a.size / 1024)} KB
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </motion.div>
    );
}

// Very lightweight HTML sanitization — strip script/style/event handlers.
function sanitizeHtml(html) {
    if (!html) return "";
    return html
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/ on\w+="[^"]*"/gi, "")
        .replace(/ on\w+='[^']*'/gi, "")
        .replace(/javascript:/gi, "");
}
